export const BROWSER_IMAGE_TYPES = new Set([
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"image/avif",
	"image/svg+xml"
]);

export type DisplayableImage = {
	contentType: string | null;
	publicUrl: string | null;
	processedImages?: { width: number; publicUrl: string }[] | null;
	processed?: boolean;
	createdAt?: string | Date | null;
};

export function isBrowserImageType(contentType: string | null | undefined): boolean {
	return !!contentType && BROWSER_IMAGE_TYPES.has(contentType.toLowerCase());
}

export function pickDisplayImageUrl(image: DisplayableImage, targetWidth: number): string | null {
	const variants = [...(image.processedImages ?? [])].sort((a, b) => a.width - b.width);
	const fit = variants.find((variant) => variant.width >= targetWidth) ?? variants.at(-1);
	if (fit) return fit.publicUrl;
	return isBrowserImageType(image.contentType) ? image.publicUrl : null;
}

export function isImagePendingOptimization(image: DisplayableImage): boolean {
	return (
		image.processed === false &&
		!!image.contentType?.toLowerCase().startsWith("image/") &&
		!isBrowserImageType(image.contentType) &&
		(image.processedImages ?? []).length === 0
	);
}

export const PENDING_POLL_MS = 3000;
export const PENDING_POLL_WINDOW_MS = 10 * 60 * 1000;

export function pendingRefetchInterval(
	images: DisplayableImage | DisplayableImage[] | null | undefined,
	now = Date.now()
): number | false {
	const list = Array.isArray(images) ? images : images ? [images] : [];
	return list.some(
		(image) =>
			isImagePendingOptimization(image) &&
			(!image.createdAt || now - new Date(image.createdAt).getTime() < PENDING_POLL_WINDOW_MS)
	)
		? PENDING_POLL_MS
		: false;
}

const IMAGE_RETRY_DELAYS_MS = [1000, 3000, 8000];

export function imageRetryDelayMs(attempt: number): number | null {
	return IMAGE_RETRY_DELAYS_MS[attempt] ?? null;
}

export function withRetryCacheBuster(url: string, attempt: number): string {
	if (attempt <= 0) return url;
	const [base, hash] = url.split("#", 2);
	const retried = `${base}${base.includes("?") ? "&" : "?"}v=r${attempt}`;
	return hash === undefined ? retried : `${retried}#${hash}`;
}
